import { useCallback, useEffect, useRef, useState } from 'react';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { useMessage } from '@src/context/common';
import {
  diagnoseFeed,
  downloadSourceFile,
  fetchFeedPipeline,
  fetchFeedRuns,
} from '@src/api/feedHealth';
import { fetchTaskLog } from '@src/api/taskLogger';

const RUN_STATUS_META = {
  ok: { label: 'Imported', color: 'success' },
  no_rows: { label: '0 rows', color: 'warning' },
  no_data: { label: 'No new data', color: 'default' },
  error: { label: 'Error', color: 'error' },
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatBytes = (size) => {
  if (!size && size !== 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const numberOrDash = (value) => (typeof value === 'number' ? value : '—');

// One step of the flow-through strip: label on top, value under it, coloured
// by whether that stage looks current.
function PipelineStep({ label, value, tone = 'default', hint }) {
  const color =
    tone === 'warning' ? 'warning.main' : tone === 'error' ? 'error.main' : 'text.primary';
  const body = (
    <Box sx={{ minWidth: 110 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color, fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );
  return hint ? <Tooltip title={hint}>{body}</Tooltip> : body;
}

function RunLogCollapse({ taskLoggerId }) {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTaskLog(taskLoggerId)
      .then(({ data }) => {
        if (!cancelled) setMessages(data?.messages || []);
      })
      .catch(() => {
        if (!cancelled) setError('Log unavailable (it may have been pruned).');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [taskLoggerId]);

  if (loading) return <CircularProgress size={16} sx={{ my: 1 }} />;
  if (error) {
    return (
      <Typography variant="caption" color="text.secondary">
        {error}
      </Typography>
    );
  }
  if (!messages?.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        No log messages recorded for this run.
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        maxHeight: 260,
        overflow: 'auto',
        bgcolor: 'action.hover',
        borderRadius: 1,
        p: 1.5,
        fontFamily: 'monospace',
        fontSize: 12,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {messages.map((msg) => (
        <Box key={msg.id}>{msg.message}</Box>
      ))}
    </Box>
  );
}

// "No runs" has three very different meanings and the operator needs to know
// which one they are looking at: the job has never run, the job runs but never
// finds a matching email, or run history simply predates the feature.
function EmptyRunHistory({ pipeline }) {
  const checkedAt = pipeline?.last_checked_at;
  if (!checkedAt) {
    return (
      <Alert severity="warning">
        <Typography variant="body2">
          No run has ever been recorded for this mapping, and the scheduler has never checked
          it. That points at the schedule itself, not the feed — confirm the mapping is enabled
          with a run time set, and that its Beat entry exists.
        </Typography>
      </Alert>
    );
  }
  const criteria = pipeline?.criteria || {};
  const foundNothing = pipeline?.last_run_status === 'no_email';
  return (
    <Alert severity={foundNothing ? 'warning' : 'info'}>
      <Stack spacing={0.75}>
        <Typography variant="body2">
          Last checked {formatDateTime(checkedAt)}
          {foundNothing
            ? ' — the mailbox search matched no email at all, so there was nothing to import.'
            : ' — no completed run is on file yet (history accumulates from each run that finds a report).'}
        </Typography>
        {foundNothing && (
          <>
            <Typography variant="body2">
              The job <strong>is</strong> running. Either the source stopped sending, or the
              report no longer matches what we search for:
            </Typography>
            <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
              from: {criteria.sender || '—'}
              {criteria.cc ? ` · cc: ${criteria.cc}` : ''}
              <br />
              subject: {criteria.subject || '—'}
              {criteria.filename_substring ? (
                <>
                  <br />
                  attachment contains: {criteria.filename_substring}
                </>
              ) : null}
            </Typography>
          </>
        )}
      </Stack>
    </Alert>
  );
}

// Run history + pipeline flow-through + AI diagnosis for one mapping. Rendered
// in the /email-mappings drawer under the health strip.
export default function FeedRunHistoryCard({ type, mappingId, autoDiagnose = false }) {
  const [runs, setRuns] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRun, setExpandedRun] = useState(null);
  const [diagnosis, setDiagnosis] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosisError, setDiagnosisError] = useState(null);
  const autoDiagnoseFired = useRef(false);
  const message = useMessage();

  useEffect(() => {
    if (!type || !mappingId) return undefined;
    let cancelled = false;
    setLoading(true);
    setRuns(null);
    setPipeline(null);
    setDiagnosis(null);
    setDiagnosisError(null);
    setExpandedRun(null);
    Promise.all([
      fetchFeedRuns({ type, id: mappingId }).then(({ data }) => data),
      fetchFeedPipeline({ type, id: mappingId }).then(({ data }) => data),
    ])
      .then(([runsData, pipelineData]) => {
        if (cancelled) return;
        setRuns(runsData?.runs || []);
        setPipeline(pipelineData || null);
        // Surface a still-fresh cached diagnosis without an extra LLM call.
        const cached = (runsData?.runs || []).find((r) => r.ai_diagnosis);
        if (cached?.ai_diagnosis) {
          setDiagnosis({ ...cached.ai_diagnosis, generated_at: cached.ai_diagnosis_at, cached: true });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRuns([]);
          setPipeline(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [type, mappingId]);

  const runDiagnosis = useCallback(
    (force = false) => {
      if (!type || !mappingId) return;
      setDiagnosing(true);
      setDiagnosisError(null);
      diagnoseFeed({ type, id: mappingId, force })
        .then(({ data }) => setDiagnosis(data))
        .catch((error) => {
          const detail = error?.response?.data?.detail;
          setDiagnosisError(detail || 'Diagnosis failed — check the run logs directly.');
        })
        .finally(() => setDiagnosing(false));
    },
    [type, mappingId],
  );

  useEffect(() => {
    if (autoDiagnose && !autoDiagnoseFired.current && !loading && mappingId) {
      autoDiagnoseFired.current = true;
      runDiagnosis(false);
    }
  }, [autoDiagnose, loading, mappingId, runDiagnosis]);

  const handleDownload = (file) => {
    downloadSourceFile(file.id)
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.filename || `source-${file.id}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => message.error('Could not download the source file.'));
  };

  if (!mappingId) return null;

  const backend = pipeline?.backend || {};
  const receipt = backend.last_summary_refresh;
  const lagDays = backend.summary_lag_days;
  const lagTone = lagDays > 0 || (backend.backend_max_date && !backend.summary_max_date)
    ? 'warning'
    : 'default';

  return (
    <Card>
      <CardHeader
        title="Runs & data flow"
        subheader="What actually happened on recent runs, and whether the data reached the dashboards."
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={diagnosing ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
            onClick={() => runDiagnosis(false)}
            disabled={diagnosing}
          >
            Diagnose with AI
          </Button>
        }
      />
      <Divider />
      <CardContent>
        {loading ? (
          <Box textAlign="center" py={2}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {(diagnosis || diagnosisError) && (
              <Alert
                severity={diagnosisError ? 'warning' : diagnosis?.likely_cause ? 'info' : 'success'}
                action={
                  !diagnosisError && (
                    <Tooltip title="Regenerate (ignores the 24h cache)">
                      <IconButton size="small" onClick={() => runDiagnosis(true)} disabled={diagnosing}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )
                }
              >
                {diagnosisError ? (
                  diagnosisError
                ) : (
                  <Stack spacing={0.75}>
                    <Typography variant="body2">{diagnosis.summary}</Typography>
                    {diagnosis.likely_cause && (
                      <Typography variant="body2">
                        <strong>Likely cause:</strong> {diagnosis.likely_cause}
                      </Typography>
                    )}
                    {diagnosis.suggested_fix && (
                      <Typography variant="body2">
                        <strong>Suggested fix:</strong> {diagnosis.suggested_fix}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {diagnosis.confidence ? `Confidence: ${diagnosis.confidence}. ` : ''}
                      {diagnosis.cached ? 'Cached — use the refresh icon to regenerate.' : 'Generated just now.'}
                    </Typography>
                  </Stack>
                )}
              </Alert>
            )}

            {/* Flow-through strip: import -> backend rows -> summaries -> dashboards */}
            {pipeline?.client_id ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Data flow
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  divider={<Typography color="text.disabled">→</Typography>}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <PipelineStep
                    label="Last run"
                    value={
                      pipeline.latest_run
                        ? `${formatDateTime(pipeline.latest_run.started_at)} · ${numberOrDash(pipeline.latest_run.rows)} rows`
                        : 'No runs recorded yet'
                    }
                    tone={pipeline.latest_run?.status === 'error' ? 'error' : pipeline.latest_run?.rows === 0 ? 'warning' : 'default'}
                  />
                  <PipelineStep
                    label="Backend data through"
                    value={backend.backend_max_date || 'None'}
                    tone={backend.backend_stale ? 'warning' : 'default'}
                    hint="Newest record date in this client's imported backend data."
                  />
                  <PipelineStep
                    label="Summaries refreshed"
                    value={
                      receipt
                        ? `${formatDateTime(receipt.refreshed_at)} (${receipt.date_min || '?'} – ${receipt.date_max || '?'}, ${numberOrDash(receipt.rows_updated)} rows)`
                        : 'No refresh recorded'
                    }
                    tone={receipt ? 'default' : 'warning'}
                    hint="Most recent rollup of backend data into the dashboard summary tables."
                  />
                  <PipelineStep
                    label="Dashboards current through"
                    value={backend.summary_max_date || 'None'}
                    tone={lagTone}
                    hint={
                      lagDays > 0
                        ? `Dashboard tables are ${lagDays} day(s) behind the imported backend data.`
                        : 'Newest date in the dashboard summary tables.'
                    }
                  />
                </Stack>
                {lagDays > 0 && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    Imported backend data reaches {backend.backend_max_date}, but the dashboard
                    summaries only cover through {backend.summary_max_date} — the rollup pipeline
                    is behind. Re-running the import (or the backend summary update) should close
                    the gap.
                  </Alert>
                )}
                {pipeline.recon?.detail && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    Open reconciliation gap: {pipeline.recon.detail}
                  </Alert>
                )}
              </Box>
            ) : (
              pipeline?.parent_scoped && (
                <Typography variant="caption" color="text.secondary">
                  Parent-scoped mapping — imported rows land on each routed child client, so
                  per-client dashboard freshness is shown on the children.
                </Typography>
              )
            )}

            {/* Recent runs */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Recent runs
              </Typography>
              {runs?.length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Rows</TableCell>
                      <TableCell align="right">Created</TableCell>
                      <TableCell align="right">Updated</TableCell>
                      <TableCell align="right">Errors</TableCell>
                      <TableCell>Source file</TableCell>
                      <TableCell align="right">Log</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runs.map((run) => {
                      const meta = RUN_STATUS_META[run.status] || { label: run.status, color: 'default' };
                      const expanded = expandedRun === run.id;
                      return [
                        <TableRow key={run.id} hover>
                          <TableCell>
                            <Typography variant="body2">{formatDateTime(run.started_at)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={run.detail || ''}>
                              <Chip size="small" color={meta.color} label={meta.label} />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{numberOrDash(run.rows)}</TableCell>
                          <TableCell align="right">{numberOrDash(run.created_rows)}</TableCell>
                          <TableCell align="right">{numberOrDash(run.updated_rows)}</TableCell>
                          <TableCell align="right">{numberOrDash(run.errors)}</TableCell>
                          <TableCell>
                            {run.source_file ? (
                              <Button
                                size="small"
                                startIcon={<DownloadIcon fontSize="small" />}
                                onClick={() => handleDownload(run.source_file)}
                                sx={{ textTransform: 'none', maxWidth: 220 }}
                              >
                                <Typography variant="caption" noWrap title={run.source_file.filename}>
                                  {run.source_file.filename}
                                  {run.source_file.size ? ` (${formatBytes(run.source_file.size)})` : ''}
                                </Typography>
                              </Button>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {run.task_logger_id ? (
                              <IconButton
                                size="small"
                                onClick={() => setExpandedRun(expanded ? null : run.id)}
                              >
                                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>,
                        expanded && (
                          <TableRow key={`${run.id}-log`}>
                            <TableCell colSpan={8} sx={{ py: 1, bgcolor: 'background.default' }}>
                              <Collapse in={expanded} unmountOnExit>
                                <RunLogCollapse taskLoggerId={run.task_logger_id} />
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        ),
                      ];
                    })}
                  </TableBody>
                </Table>
              ) : (
                <EmptyRunHistory pipeline={pipeline} />
              )}
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
